import { useState } from 'react';
import { cn } from '../utils/cn';
import { ChevronDown } from 'lucide-react';

interface StatusDropdownProps {
  currentStatus: string;
  storyId: string;
  onStatusChange: (storyId: string, newStatus: string) => void;
  disabled?: boolean;
  className?: string;
  isStoryComplete?: boolean;
}

/**
 * Unified status dropdown component for both mobile and desktop
 * @param {StatusDropdownProps} props - Component props
 * @returns {JSX.Element} Status dropdown
 */
export function StatusDropdown({ 
  currentStatus, 
  storyId, 
  onStatusChange, 
  disabled = false,
  className,
  isStoryComplete = true
}: StatusDropdownProps) {
  const [isChanging, setIsChanging] = useState(false);
  
  const statusOptions = [
    { value: 'draft', label: 'Draft', disabled: false },
    { 
      value: 'ready_to_publish', 
      label: 'Ready to Publish',
      disabled: !isStoryComplete,
      tooltip: !isStoryComplete ? 'Complete all slides before marking as ready to publish' : undefined
    },
    { value: 'published', label: 'Published', disabled: false }
  ];
  
  const statusColors = {
    draft: 'text-gray-700 bg-gray-50',
    ready_to_publish: 'text-yellow-700 bg-yellow-50',
    published: 'text-green-700 bg-green-50',
  };

  /**
   * Handle status change
   * @param {React.ChangeEvent<HTMLSelectElement>} e - Change event
   */
  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    if (newStatus === currentStatus) return;
    
    setIsChanging(true);
    try {
      await onStatusChange(storyId, newStatus);
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div className={cn("relative", className)}>
      <select
        value={currentStatus}
        onChange={handleChange}
        disabled={disabled || isChanging || currentStatus === 'published'}
        className={cn(
          "appearance-none w-full rounded-md border px-3 py-2 pr-8 text-sm font-medium",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
          "transition-colors cursor-pointer touch-target",
          statusColors[currentStatus as keyof typeof statusColors] || 'text-gray-700 bg-gray-50',
          "border-gray-300",
          (disabled || currentStatus === 'published') && "opacity-50 cursor-not-allowed",
          isChanging && "opacity-75 cursor-wait"
        )}
      >
        {statusOptions.map(option => (
          <option 
            key={option.value} 
            value={option.value}
            disabled={option.disabled}
            title={option.tooltip}
          >
            {option.label}
            {option.disabled && ' (Complete all slides first)'}
          </option>
        ))}
      </select>
      
      {/* Custom dropdown arrow */}
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
        <ChevronDown 
          className={cn(
            "h-4 w-4",
            statusColors[currentStatus as keyof typeof statusColors]?.split(' ')[0] || 'text-gray-700'
          )} 
        />
      </div>
      
      {/* Loading indicator */}
      {isChanging && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 rounded-md">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}
    </div>
  );
}